/**
 * Die Leerzustände einer Wohnung, die Übersicht und „Strom" gemeinsam haben
 * (HM-083). Zwei Ansichten, ein Satz — sonst sagte die eine „vor deinem
 * Einzug" und die andere etwas anderes.
 */
import { Button, Card, EmptyState, icons } from "@hausmeter/ui";
import type { ApiComponents } from "@hausmeter/sdk";
import { formatDate } from "../lib/format.js";

type UnitEnergy = ApiComponents["schemas"]["UnitEnergyResponse"];

/** Ohne Messkonzept, das diese Einheit führt — mit dem Weg dorthin, wenn der Aufrufer einen hat. */
export function UnitNoConcept({ title, onConcept }: { title: string; onConcept?: () => void }) {
  return (
    <Card title={title} subtitle="Verbrauch, PV-Anteil und Netzanteil dieser Wohneinheit">
      <EmptyState
        title="Noch keine Werte für diese Wohneinheit"
        icon={<icons.IconTemplate size={30} />}
        size="md"
        action={
          onConcept === undefined ? undefined : (
            <Button variant="primary" size="sm" onClick={onConcept}>
              Zum Messkonzept
            </Button>
          )
        }
      >
        Die Werte einer Wohnung entstehen aus dem <strong>Messkonzept</strong> der Anlage — der
        PV-Anteil ist eine Rechnung über das ganze Haus. Sie erscheinen, sobald ein
        Mieterstrom-Konzept diese Wohneinheit als Steckplatz führt und ihr Zähler dort steckt.
      </EmptyState>
    </Card>
  );
}

/**
 * **Ein Zeitraum ganz außerhalb der Bewohnung ist leer und sagt warum** (HM-081).
 * Vor dem Einzug und nach dem Auszug gibt es für den Bewohner nichts zu sehen —
 * und ein leeres Diagramm ohne Satz sähe aus wie ein Ausfall.
 */
export function isOutsideOccupancy(data: UnitEnergy): boolean {
  return data.occupancy !== null && data.clipped && new Date(data.to).getTime() <= new Date(data.from).getTime();
}

export function UnitOutsideOccupancy({ title, data }: { title: string; data: UnitEnergy }) {
  if (data.occupancy === null) return null;
  const before = data.period !== null && data.period.to <= data.occupancy.from;
  return (
    <Card title={title} subtitle="Verbrauch, PV-Anteil und Netzanteil dieser Wohneinheit">
      <EmptyState title={before ? "Vor deinem Einzug" : "Nach deinem Auszug"} icon={<icons.IconDoor size={30} />} size="md">
        {before
          ? `Dieser Zeitraum liegt vor deinem Einzug am ${formatDate(data.occupancy.movedInOn)}.`
          : `Dieser Zeitraum liegt nach deinem Auszug am ${data.occupancy.movedOutOn === null ? "" : formatDate(data.occupancy.movedOutOn)}.`}{" "}
        Du siehst die Werte deiner Wohnung für die Zeit, in der du dort wohnst.
      </EmptyState>
    </Card>
  );
}
